/**
 * The proposal record.
 *
 * Everything HR saw, everything that happened, and what can happen next. Two things here are
 * not decoration:
 *
 *   - **The attendee table is the snapshot, not today's figures.** It is frozen at what HR was
 *     shown when they committed the money, so it still reads unheld even after the holds are
 *     written. The live figure lives on the proposal form; this is the evidence.
 *   - **The audit trail is shown in full**, because "who approved this and when" is the question
 *     that gets asked six months later.
 */
import { useState } from 'react';
import { useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useSession } from '@/hooks/session';
import { ProposalActions } from '@/components/ProposalActions';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { AmendDialog } from '@/components/AmendDialog';
import { Clarifications } from '@/components/Clarifications';
import { AuditTrail } from '@/components/AuditTrail';
import { Deadline, ErrorNote, Money, PageHeader, ProposalStatusBadge } from '@/components/bits';
import { Alert, Badge, Button, Card, Facts, Table, Td, Th } from '@/components/ui';
import { activityLabel, describeNonContribution } from '@/lib/policy';
import { formatDate, formatInstant } from '@/lib/dates';

export function ProposalDetail() {
  const params = useParams();
  const id = Number(params.id);
  const session = useSession();
  const cache = useQueryClient();

  const [confirming, setConfirming] = useState(false);
  const [amending, setAmending] = useState(false);

  const proposal = useQuery({ queryKey: ['proposal', id], queryFn: () => api.proposal(id) });

  const decide = useMutation({
    mutationFn: (input: { exceptionId: number; decision: 'confirmed' | 'declined' }) =>
      api.decideProposalException(id, input.exceptionId, { decision: input.decision }),
    onSuccess: () => cache.invalidateQueries({ queryKey: ['proposal', id] }),
  });

  if (proposal.isPending) return <p className="text-muted-foreground text-sm">Loading…</p>;
  if (proposal.error) return <ErrorNote error={proposal.error} />;

  const record = proposal.data;
  if (!record) return null;

  return (
    <>
      <PageHeader
        title={
          <span className="flex flex-wrap items-center gap-3">
            {record.ref_no ?? 'Draft proposal'}
            <ProposalStatusBadge status={record.status} />
            {record.pending_exceptions > 0 ? (
              <Badge tone="warn">
                {record.pending_exceptions} pending{' '}
                {record.pending_exceptions === 1 ? 'exception' : 'exceptions'}
              </Badge>
            ) : null}
          </span>
        }
        subtitle={`${record.activity_title} · ${record.department.name ?? ''}`}
        actions={
          <ProposalActions
            proposal={record}
            onConfirm={() => setConfirming(true)}
            onAmend={() => setAmending(true)}
          />
        }
      />

      {record.returned_reason ? (
        <Alert tone="warn" title="Returned for changes" className="mb-6">
          {record.returned_reason}
        </Alert>
      ) : null}
      {record.rejection_reason ? (
        <Alert tone="bad" title="Rejected" className="mb-6">
          {record.rejection_reason}
        </Alert>
      ) : null}
      {record.cancellation_reason ? (
        <Alert tone="neutral" title="Cancelled" className="mb-6">
          {record.cancellation_reason}
        </Alert>
      ) : null}
      {record.sponsor_skipped ? (
        <Alert tone="neutral" title="Sponsor endorsement was skipped" className="mb-6">
          The person who raised this is a Sponsor of the department, so endorsing their own proposal
          would be one signature pretending to be two (D-39). It went straight to HR.
        </Alert>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem] lg:items-start">
        <div className="grid min-w-0 gap-6">
          <Card title="The event">
            <Facts
              items={[
                { label: 'Activity', value: activityLabel(record.activity_type) },
                {
                  label: 'Date',
                  value: record.proposed_end_date
                    ? `${formatDate(record.proposed_date)} – ${formatDate(record.proposed_end_date)}`
                    : formatDate(record.proposed_date),
                },
                { label: 'Venue', value: record.venue ?? '—' },
                { label: 'Who it is for', value: record.scope_note ?? '—' },
                { label: 'Remark', value: record.remark ?? '—' },
                {
                  label: 'Raised',
                  value: record.submitted_at
                    ? formatInstant(record.submitted_at)
                    : `created ${formatInstant(record.created_at)}, not yet submitted`,
                },
              ]}
            />
          </Card>

          <Card
            title={`The namelist HR saw — ${record.attendees.length} people`}
            action={
              record.attendees.length > 0 ? (
                <span className="text-muted-foreground text-xs">
                  Frozen at submission. Not today&rsquo;s figures.
                </span>
              ) : null
            }
          >
            {record.attendees.length === 0 ? (
              <p className="text-muted-foreground text-sm">Nobody has been added yet.</p>
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>Name</Th>
                    <Th>No.</Th>
                    <Th>Department</Th>
                    <Th numeric>Service</Th>
                    <Th numeric>Cap</Th>
                    <Th numeric>Used</Th>
                    <Th numeric>Held</Th>
                    <Th numeric>Available</Th>
                  </tr>
                </thead>
                <tbody>
                  {record.attendees.map((attendee) => (
                    <tr
                      key={attendee.employee_id}
                      className={attendee.contributes ? '' : 'text-muted-foreground'}
                    >
                      <Td>
                        <span className={attendee.contributes ? 'font-medium' : ''}>
                          {attendee.full_name}
                        </span>
                        {attendee.in_department ? null : (
                          <span className="ml-2">
                            <Badge>from {attendee.department_name ?? 'elsewhere'}</Badge>
                          </span>
                        )}
                        {attendee.non_contribution_reason ? (
                          <p className="text-xs">
                            {describeNonContribution(attendee.non_contribution_reason)}
                          </p>
                        ) : null}
                      </Td>
                      <Td className="tabular text-xs">{attendee.employee_no}</Td>
                      <Td className="text-xs">{attendee.department_name ?? '—'}</Td>
                      <Td numeric className="text-xs">
                        {attendee.service_months} mo
                      </Td>
                      <Td numeric>
                        <Money cents={attendee.cap_cents} />
                      </Td>
                      <Td numeric>
                        <Money cents={attendee.used_cents} muted />
                      </Td>
                      <Td numeric>
                        <Money cents={attendee.held_cents} muted />
                      </Td>
                      <Td numeric>
                        <Money cents={attendee.available_cents} />
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card>

          {record.exceptions.length > 0 ? (
            <Card title="Exceptions">
              <ul className="grid gap-4">
                {record.exceptions.map((exception) => (
                  <li key={exception.id} className="grid gap-1.5 text-sm">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge
                        tone={
                          exception.decision === 'pending'
                            ? 'warn'
                            : exception.decision === 'confirmed'
                              ? 'good'
                              : 'bad'
                        }
                      >
                        {exception.rule_id} · {exception.decision}
                      </Badge>
                      <span className="text-muted-foreground text-xs">
                        raised {formatInstant(exception.raised_at)}
                      </span>
                    </div>
                    <p>{exception.justification}</p>
                    {exception.decision_note ? (
                      <p className="text-muted-foreground text-xs">HR: {exception.decision_note}</p>
                    ) : null}
                    {exception.decision === 'pending' && session.isHr ? (
                      <div className="mt-1 flex gap-2">
                        <Button
                          size="sm"
                          variant="primary"
                          busy={decide.isPending}
                          onClick={() =>
                            decide.mutate({ exceptionId: exception.id, decision: 'confirmed' })
                          }
                        >
                          Accept
                        </Button>
                        <Button
                          size="sm"
                          busy={decide.isPending}
                          onClick={() =>
                            decide.mutate({ exceptionId: exception.id, decision: 'declined' })
                          }
                        >
                          Decline
                        </Button>
                      </div>
                    ) : null}
                  </li>
                ))}
              </ul>
              {record.pending_exceptions > 0 ? (
                <Alert tone="warn" title="The budget cannot be confirmed yet" className="mt-4">
                  An exception is outstanding. HR decides it first — enforced in the service and by
                  a database trigger, so no screen can route around it.
                </Alert>
              ) : null}
              {decide.error ? <ErrorNote error={decide.error} /> : null}
            </Card>
          ) : null}

          {record.amendments.length > 0 ? (
            <Card title="HR changed the namelist">
              <ul className="grid gap-2 text-sm">
                {record.amendments.map((amendment) => (
                  <li key={amendment.id}>
                    <Badge tone={amendment.action === 'added' ? 'good' : 'warn'}>
                      {amendment.action}
                    </Badge>{' '}
                    {amendment.employee_name} — {amendment.reason}
                    <span className="text-muted-foreground ml-2 text-xs">
                      {formatInstant(amendment.created_at)}
                    </span>
                  </li>
                ))}
              </ul>
            </Card>
          ) : null}

          <Clarifications
            threads={record.clarifications}
            canAnswer={record.available_actions.includes('answer_clarification')}
            onReply={(clarificationId, body) =>
              api.replyProposalClarification(record.id, clarificationId, { body })
            }
            onClose={(clarificationId) =>
              api.closeProposalClarification(record.id, clarificationId)
            }
            invalidate={['proposal', record.id]}
          />

          <AuditTrail entries={record.audit} />
        </div>

        <div className="grid gap-4 lg:sticky lg:top-28">
          <Card title="Money">
            <Facts
              items={[
                {
                  label: 'Pool when submitted',
                  value: <Money cents={record.computed_pool_cents} />,
                },
                { label: 'Requested', value: <Money cents={record.requested_amount_cents} /> },
                {
                  label: 'Approved',
                  value: <Money cents={record.approved_amount_cents} />,
                },
                { label: 'Claim by', value: <Deadline date={record.claim_deadline} /> },
              ]}
            />
            {record.approved_amount_cents !== null ? (
              <p className="text-muted-foreground mt-3 text-xs">
                The approved amount is a <strong>reservation</strong>, not a ceiling on the claim.
                The claim is settled against what the people who actually attended had available on
                the day (D-43).
              </p>
            ) : null}
          </Card>

          <Card title="Participation">
            <Facts
              items={[
                { label: 'Attendees', value: record.attendee_count ?? '—' },
                { label: 'From the department', value: record.in_department_count ?? '—' },
                { label: 'Department size', value: record.department_headcount ?? '—' },
                {
                  label: 'Share',
                  value:
                    record.participation_pct === null
                      ? '—'
                      : `${Math.round(record.participation_pct)}%`,
                },
              ]}
            />
          </Card>
        </div>
      </div>

      <ConfirmDialog
        open={confirming}
        proposalId={record.id}
        onClose={() => setConfirming(false)}
      />
      <AmendDialog open={amending} proposal={record} onClose={() => setAmending(false)} />
    </>
  );
}
